import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import Markdown from "markdown-to-jsx";

import { Avatar, Box, IconButton, Paper, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";

import { askWilco, WilcoError, WilcoMessage, wilcoApiUrl } from "src/api/wilco";
import { helpBaseUrl, helpPageFor, helpUrl } from "src/services/help";
import { DEFAULT_LANGUAGE, normalizeLanguage } from "src/services/localization";
import { captureHandledException } from "src/services/sentry";

import wilcoImage from "../../assets/wilco.png";

const EDGE = 24;
// The button sits above CO's fixed footer (components/Footer/styles.scss); the open panel may cover the footer,
// which leaves short screens (a phone in landscape) more room for the conversation
const BOTTOM = 40 + EDGE;
// NGFT primary blue (design tokens), the colour of Wilco in the other NGFT apps; CO's own primary is grey
const WILCO_BLUE = "#1C79E5";
const spin = keyframes`to { transform: rotate(360deg); }`;

// Answers link chapters by page file ("members.html"): open them in the tutorial, in a new tab, in a language the
// tutorial has (helpUrl, as the ? button). Where the tutorial is not published they stay plain text.
// markdown-to-jsx passes href null for a URL its sanitizer rejects (javascript:, data:, a bad %-escape).
const ChapterLink = ({ href, children }: { href?: string | null; children?: React.ReactNode }) => {
  const url = href ?? "";
  const chapter = url.match(/^([a-z0-9-]+)\.html(#[\w-]*)?$/);
  if (chapter ? !helpBaseUrl() : !/^https?:\/\//.test(url)) return <>{children}</>;
  return (
    <a href={chapter ? helpUrl(chapter[1]) + (chapter[2] ?? "") : url} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

// Code keeps only its text: a fence's info string can carry attributes, e.g. a style with a url() the browser loads.
const PlainCode = ({ children }: { children?: React.ReactNode }) => <code>{children}</code>;

// The model's text is never parsed as HTML, and its images are dropped: the browser would load any URL they name.
const MARKDOWN_OPTIONS = {
  disableParsingRawHTML: true,
  overrides: { a: ChapterLink, img: () => null, code: PlainCode },
};

const Bubble = ({ message }: { message: WilcoMessage }) => {
  const mine = message.role === "user";
  return (
    <Box
      sx={{
        alignSelf: mine ? "flex-end" : "flex-start",
        maxWidth: "85%",
        px: 1.5,
        py: 1,
        borderRadius: 2,
        bgcolor: mine ? "primary.main" : "grey.100",
        color: mine ? "primary.contrastText" : "text.primary",
        fontSize: 14,
        wordBreak: "break-word",
        // The question keeps the line breaks typed with Shift+Enter
        whiteSpace: mine ? "pre-wrap" : "normal",
        "& p": { my: 0.5 },
        // reset.scss removes list markers and bold/italic/code fonts; answers need them for their steps
        "& ol, & ul": { my: 0.5, pl: 2.5 },
        "& ol": { listStyle: "decimal" },
        "& ul": { listStyle: "disc" },
        "& strong": { fontWeight: 600 },
        "& em": { fontStyle: "italic" },
        "& code": { fontFamily: "monospace" },
        "& a": { color: "inherit" },
      }}
    >
      {mine ? message.content : <Markdown options={MARKDOWN_OPTIONS}>{message.content}</Markdown>}
    </Box>
  );
};

/** Wilco: the NGFT Crew help assistant, answering from the tutorial. Shown only where ngft-ai is configured. */
const Wilco = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<WilcoMessage[]>([]);
  const [question, setQuestion] = useState("");
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [open, messages, waiting, error]);

  if (!wilcoApiUrl()) return null;

  const send = async () => {
    const text = question.trim();
    if (!text || waiting) return;
    const conversation: WilcoMessage[] = [...messages, { role: "user", content: text }];
    setMessages(conversation);
    setQuestion("");
    setError("");
    setWaiting(true);
    const page = helpPageFor(pathname);
    try {
      const answer = await askWilco(
        conversation,
        page === "index" ? undefined : page,
        normalizeLanguage(i18next.language) ?? DEFAULT_LANGUAGE,
      );
      setMessages([...conversation, { role: "assistant", content: answer }]);
    } catch (e) {
      // ngft-ai logs its own HTTP errors; network failures and a broken answer only show here
      captureHandledException(e, { feature: "wilco", component: "Wilco", action: "ask" });
      setError(t(e instanceof WilcoError && e.status === 429 ? "wilco.error_busy" : "wilco.error_unavailable"));
    } finally {
      setWaiting(false);
    }
  };

  if (!open) {
    return (
      <Tooltip title={t("wilco.open")}>
        <IconButton
          aria-label={t("wilco.open")}
          onClick={() => setOpen(true)}
          sx={{ position: "fixed", right: EDGE, bottom: BOTTOM, zIndex: 1200, p: 0, width: 48, height: 48 }}
        >
          {/* As in the SMS frontend: a light ring turning around the avatar */}
          <Box
            sx={{
              position: "absolute",
              inset: -2,
              borderRadius: "50%",
              background: `linear-gradient(90deg, transparent, ${WILCO_BLUE}80, transparent)`,
              animation: `${spin} 3s linear infinite`,
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          />
          <Avatar
            src={wilcoImage}
            alt=""
            sx={{
              width: 48,
              height: 48,
              border: `2px solid ${WILCO_BLUE}`,
              bgcolor: "common.white",
              boxShadow: 5,
              transition: "transform 0.15s",
              "&:hover": { transform: "scale(1.05)" },
            }}
          />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Paper
      role="dialog"
      aria-label={String(t("wilco.title"))}
      elevation={8}
      sx={{
        position: "fixed",
        right: EDGE,
        bottom: EDGE,
        zIndex: 1200,
        width: `min(380px, calc(100vw - ${2 * EDGE}px))`,
        height: `min(560px, calc(100vh - ${4 * EDGE}px))`,
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: "center", px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}
      >
        <Avatar src={wilcoImage} alt="" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {t("wilco.title")}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {t("wilco.subtitle")}
          </Typography>
        </Box>
        <IconButton aria-label={t("wilco.close")} onClick={() => setOpen(false)} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Stack spacing={1} sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        <Bubble message={{ role: "assistant", content: t("wilco.welcome") }} />
        {messages.map((message, index) => (
          <Bubble key={index} message={message} />
        ))}
        {waiting && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {t("wilco.thinking")}
          </Typography>
        )}
        {error && (
          <Typography variant="caption" color="error" role="alert">
            {error}
          </Typography>
        )}
        <div ref={endRef} />
      </Stack>

      <Box sx={{ p: 1.5, borderTop: 1, borderColor: "divider" }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "flex-end" }}>
          <TextField
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={t("wilco.placeholder")}
            slotProps={{ htmlInput: { "aria-label": t("wilco.placeholder"), maxLength: 2000 } }}
            multiline
            maxRows={4}
            size="small"
            fullWidth
            autoFocus
          />
          <IconButton
            aria-label={t("wilco.send")}
            onClick={send}
            disabled={!question.trim() || waiting}
            color="primary"
          >
            <ArrowUpwardIcon />
          </IconButton>
        </Stack>
        <Typography variant="caption" component="div" sx={{ mt: 0.5, color: "text.secondary" }}>
          {t("wilco.disclaimer")}
        </Typography>
      </Box>
    </Paper>
  );
};

export default Wilco;
